// 노쇠 관리 가이드(권장 운동·식단, 2단계) 조회와 보내기(3단계). 서버가 (결과, 카탈로그·프롬프트 버전) 마다 한 번 만들어 캐시한다.
// 브리핑(useFrailBriefing)과 같은 모양 — 카드가 먼저 뜨고 가이드는 뒤에 온다. 서버 플래그가 꺼져 있으면 status 'disabled'.
// send(message) 는 담당자가 맞춤 운동 프로그램을 검토한 뒤 누른다 — POST .../frail/guide/:id/send. 결과는 sendResult, 발송 상태는 sent 로 온다.
// preview(message) 는 사용자가 받을 화면(프로그램 HTML)을 그대로 받아 온다 — GET .../frail/guide/:id/preview?message=. 카드가 iframe 에 넣는다.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/config';

const EMPTY = { status: 'idle', id: null, guide: null, createdAt: null, cached: false, error: null, sent: null };

export default function useFrailGuide(appUserId, indexId, enabled = true) {
    const [state, setState] = useState(EMPTY);
    const [nonce, setNonce] = useState(0);
    const [refresh, setRefresh] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    useEffect(() => {
        if (!appUserId || !indexId || !enabled) { setState(EMPTY); return undefined; }
        let cancelled = false;
        setState((s) => ({ ...s, status: 'loading', error: null }));
        setSendResult(null);
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/api/patients/${appUserId}/frail/guide`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: refresh ? { refresh: '1' } : {},
                    timeout: 180000
                });
                if (cancelled) return;
                const data = response.data || {};
                setState({ status: data.status || 'ready', id: data.id || null, guide: data.guide || null, createdAt: data.createdAt || null, cached: data.cached === true, error: null, sent: data.sent || null });
            } catch (error) {
                if (!cancelled) setState({ ...EMPTY, status: 'failed', error: error.response?.data?.message || error.message });
            }
        })();
        return () => { cancelled = true; };
    }, [appUserId, indexId, enabled, nonce, refresh]);

    const regenerate = useCallback(() => { setRefresh(true); setNonce((n) => n + 1); }, []);

    const NOT_SAVED = '가이드가 저장되지 않아 보낼 수 없습니다. 카드를 다시 열어 주세요.';

    const send = useCallback(async (message) => {
        if (!appUserId || sending) return null;
        if (!state.id) { const failed = { status: 'error', error: NOT_SAVED }; setSendResult(failed); return failed; }
        setSending(true);
        setSendResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_BASE_URL}/api/patients/${appUserId}/frail/guide/${state.id}/send`, { message: message || null }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 60000
            });
            const result = response.data || {};
            setSendResult(result);
            setState((s) => ({
                ...s,
                sent: {
                    sentAt: result.sentAt || s.sent?.sentAt || null,
                    sentCount: (s.sent?.sentCount || 0) + 1,
                    expiresOn: result.expiresAt ? String(result.expiresAt).slice(0, 10) : s.sent?.expiresOn || null,
                    userMessage: message || null,
                    viewCount: s.sent?.viewCount || 0,
                    lastViewedAt: s.sent?.lastViewedAt || null,
                    url: result.url || s.sent?.url || null
                }
            }));
            return result;
        } catch (error) {
            const failed = { status: 'error', error: error.response?.data?.error?.message || error.response?.data?.message || error.message };
            setSendResult(failed);
            return failed;
        } finally {
            setSending(false);
        }
    }, [appUserId, state.id, sending]);

    const preview = useCallback(async (message) => {
        if (!appUserId) return null;
        if (!state.id) throw new Error(NOT_SAVED);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/api/patients/${appUserId}/frail/guide/${state.id}/preview`, {
            headers: { Authorization: `Bearer ${token}` },
            params: message ? { message } : {},
            responseType: 'text',
            transformResponse: [(data) => data],
            timeout: 30000
        });
        return typeof response.data === 'string' ? response.data : String(response.data || '');
    }, [appUserId, state.id]);

    return { ...state, regenerate, send, sending, sendResult, preview };
}
